import { motion } from "framer-motion";
import { Button } from "../ui/button";
import { X, Check } from "lucide-react";

const Problem = () => {
  const scrollToSection = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const problems = [
    "Acumular conhecimento sem usar",
    "Praticar de forma aleatória",
    "Conversar sem feedback estratégico",
    "Ter medo de errar na frente dos outros",
  ];

  const solutions = [
    "Prática guiada todos os dias",
    "Correção que ensina a construir resposta",
    "Simulações de situações reais",
    "Progresso medido por habilidade — não por tempo",
  ];

  return (
    <section id="beneficios" className="py-20 md:py-32 bg-card relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      </div>

      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Você não está travado por não estudar.{" "}
            <span className="text-gradient">Você está travado por falta de prática de fala estruturada!</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Falar exige repetição guiada, ambiente seguro e desenvolvimento de habilidades reais — não teoria infinita.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Problems */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="card-glass rounded-2xl p-8"
          >
            <h3 className="font-display text-xl font-bold text-destructive mb-6 flex items-center gap-2">
              ❌ O que mantém você travado
            </h3>
            <ul className="space-y-4">
              {problems.map((problem, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-start gap-3 text-muted-foreground"
                >
                  <span className="w-6 h-6 rounded-full bg-destructive/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <X className="w-3 h-3 text-destructive" />
                  </span>
                  {problem}
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Solutions */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="card-glass rounded-2xl p-8 border-primary/30"
          >
            <h3 className="font-display text-xl font-bold text-primary mb-6 flex items-center gap-2">
              ✅ O que desenvolve confiança real
            </h3>
            <ul className="space-y-4">
              {solutions.map((solution, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-start gap-3 text-foreground"
                >
                  <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-primary" />
                  </span>
                  {solution}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-12"
        >
          <Button type="button" variant="cta" size="xl" onClick={(e) => scrollToSection(e, 'beneficios')}>
            👉 Começar prática estruturada
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default Problem;
